import * as React from 'react'
import { ReactElement, useEffect, useMemo, useRef, useState } from 'react'
import { Input, InputProps, makeStyles, tokens } from '@fluentui/react-components'
import { CheckmarkCircleRegular, EditRegular, ErrorCircleRegular, SaveSyncRegular } from '@fluentui/react-icons'

export enum StatusIcon {
    None = 'NONE',
    Success = 'SUCCESS',
    Failed = 'FAILED',
    Editing = 'EDITING',
    Saving = 'SAVING',
}

const useStyles = makeStyles({
    prefix: {
        color: tokens.colorNeutralForeground3,
        whiteSpace: 'nowrap',
    },
    success: { color: tokens.colorPaletteGreenForeground1 },
    failed: { color: tokens.colorPaletteRedForeground1 },
    neutral: { color: tokens.colorNeutralForeground3 },
})

export type EditableEntityInputProps = Omit<InputProps, 'onChange' | 'prefix'> & {
    /** Rendered inside the field, before the value (v9 replacement for the v8 TextField `prefix`). */
    prefix?: string
    statusIcon?: StatusIcon
    save?: (value: string) => Promise<StatusIcon | undefined>
    onFormat?: (value: string) => string
}

/**
 * A v9 `Input` that saves on blur and shows a status icon (Success/Failed/Editing/Saving).
 *
 * Migrated from the v8 `EditableTextField`; keeps the value-sync guard that `EntityField`
 * relies on: while the field is being edited or saved the locally typed value is preserved,
 * but once the status settles (None/Failed/Success) it re-syncs from `props.value` so the
 * parent search logic can reprogram the displayed code/name.
 */
export default function EditableEntityInput(props: EditableEntityInputProps): ReactElement {
    const { prefix, statusIcon, save, onFormat, contentBefore, contentAfter, ...inputProps } = props
    const styles = useStyles()
    const ref = useRef<HTMLInputElement>(null)

    const [value, setValue] = useState<string>(props.value ?? '')
    const [icon, setIcon] = useState<StatusIcon>(statusIcon || (save ? StatusIcon.Success : StatusIcon.None))

    useEffect(() => {
        if (statusIcon) {
            setIcon(statusIcon)
        }
    }, [props.value, statusIcon])

    useEffect(() => {
        if ([StatusIcon.None, StatusIcon.Failed, StatusIcon.Success].includes(icon)) {
            setValue(props.value ?? '')
        }
    }, [props.value, icon])

    const statusIconElement = useMemo<InputProps['contentAfter']>(() => {
        if (!save || icon === StatusIcon.None) {
            return contentAfter
        }
        switch (icon) {
            case StatusIcon.Success:
                return <CheckmarkCircleRegular className={styles.success} />
            case StatusIcon.Failed:
                return <ErrorCircleRegular className={styles.failed} />
            case StatusIcon.Editing:
                return <EditRegular className={styles.neutral} />
            case StatusIcon.Saving:
                return <SaveSyncRegular className={styles.neutral} />
            default:
                return contentAfter
        }
    }, [icon, save, contentAfter, styles])

    return (
        <Input
            {...inputProps}
            ref={ref}
            value={value}
            contentBefore={prefix ? <span className={styles.prefix}>{prefix}</span> : contentBefore}
            contentAfter={statusIconElement}
            onChange={(_event, data) => setValue(onFormat?.(data.value) ?? data.value ?? '')}
            onFocus={event => {
                if (save) {
                    setIcon(StatusIcon.Editing)
                }
                props.onFocus?.(event)
            }}
            onBlur={event => {
                if (save) {
                    setIcon(StatusIcon.Saving)
                    save(value).then(
                        (result?: StatusIcon) => setIcon(result || StatusIcon.Success),
                        () => setIcon(StatusIcon.Failed),
                    )
                }
                props.onBlur?.(event)
            }}
            onKeyDown={event => {
                if (save) {
                    switch (event.key) {
                        case 'Escape':
                            setValue(props.value ?? '')
                            ref.current?.blur()
                            break
                        case 'Enter':
                            ref.current?.blur()
                            break
                    }
                }
                props.onKeyDown?.(event)
            }}
        />
    )
}
